/**
 * This file is part of dHealth dApps Framework shared under LGPL-3.0
 * Copyright (C) 2022-present dHealth Network, All rights reserved.
 *
 * @package     dHealth dApps Framework
 * @subpackage  Vue Frontend
 * @author      dHealth Network <devs@dhealth.foundation>
 * @license     LGPL-3.0
 */

// external dependencies
import { Component } from "vue-property-decorator";
import InlineSvg from "vue-inline-svg";
import { mapGetters } from "vuex";

// internal dependencies
import { MetaView } from "@/views/MetaView";
import {
  UserDataAggregateDTO,
  UserStatisticsDTO,
} from "@/models/UserStatisticsDTO";
import { ConfigDTO } from "@/models/ConfigDTO";

// child components
import TopActivities from "../TopActivities/TopActivities.vue";
import ProgressBar from "../ProgressBar/ProgressBar.vue";
import InfoTip from "../InfoTip/InfoTip.vue";
import { DappIcon } from "@dhealth/components";

// style resource
import "./Stats.scss";
import { Address } from "@dhealth/sdk";

@Component({
  components: {
    InlineSvg,
    TopActivities,
    ProgressBar,
    InfoTip,
    DappIcon,
  },
  computed: {
    ...mapGetters({
      currentUserAddress: "auth/getCurrentUserAddress",
      userStatistics: "statistics/getUserStatistics",
      appConfig: "app/getConfig",
      isEarlyAdopter: "auth/isEarlyAdopter",
      nativeActivBalance: "statistics/getActivBalance",
      web3ActivBalance: "web3/getActivBalance",
      web3: "auth/isWeb3",
    }),
  },
})
export default class Stats extends MetaView {
  /**
   * This property contains the authenticated user's dHealth Account
   * Address. This field is populated using the Vuex Store after a
   * successful request to the backend API's `/me` endpoint.
   * <br /><br />
   * The `!`-operator tells TypeScript that this value is required
   * and the *public* access permits the Vuex Store to mutate this
   * value when it is necessary.
   *
   * @access public
   * @var {string}
   */
  public currentUserAddress!: string;

  /**
   * This property contains the user statistics and maps to a store
   * getter named `statistics/getUserStatistics`.
   * <br /><br />
   * The `!`-operator tells TypeScript that this value is required
   * and the *public* access permits the Vuex Store to mutate this
   * value when it is necessary.
   *
   * @access public
   * @var {UserStatisticsDTO}
   */
  public userStatistics!: UserStatisticsDTO;

  /**
   * This property contains the app configuration. The configuration
   * field `referralLevels` is used in translation parameters.
   * <br /><br />
   * The `!`-operator tells TypeScript that this value is required
   * and the *public* access permits the Vuex Store to mutate this
   * value when it is necessary.
   *
   * @access public
   * @var {ConfigDTO}
   */
  public appConfig!: ConfigDTO;

  /**
   * @todo missing property documentation
   * @todo this could also be a Prop assigned by parent
   */
  protected numReferralSteps: number = 3;

  /**
   *
   */
  private hasRequested: boolean = false;

  /**
   * This property contains the user's early adoption status and maps
   * to a store getter named `auth/isEarlyAdopter`.
   *
   * @access private
   * @var {boolean}
   */
  private isEarlyAdopter!: boolean;

  private web3!: boolean;

  /**
   * This property reflects the current ACTIV balance of user's account.
   *
   * @access private
   * @var {number}
   */
  private nativeActivBalance!: number;

  private web3ActivBalance!: number;

  /**
   * @todo missing method documentation
   */
  public get statisticsData(): UserDataAggregateDTO | undefined {
    if (
      !this.hasRequested ||
      !this.userStatistics ||
      !this.userStatistics.data
    ) {
      return undefined;
    }

    return this.userStatistics.data;
  }

  /**
   * @todo missing method documentation
   */
  public get totalEarned(): number {
    if (!this.hasRequested || undefined === this.statisticsData) {
      return 0;
    }

    return this.formatAmount(
      this.statisticsData.totalEarned ?? this.userStatistics.amount ?? 0
    );
  }

  /**
   * @todo missing method documentation
   */
  public get totalPracticedMinutes(): number {
    if (!this.hasRequested || undefined === this.statisticsData) {
      return 0;
    }

    return this.statisticsData.totalPracticedMinutes ?? 0;
  }

  /**
   * @todo missing method documentation
   */
  public get totalReferral(): number {
    if (!this.hasRequested || undefined === this.statisticsData) {
      return 0;
    }

    return this.statisticsData.totalReferral ?? 0;
  }

  /**
   * @todo missing method documentation
   */
  public get levelReferral(): number {
    if (
      !this.hasRequested ||
      undefined === this.appConfig ||
      !this.appConfig.referralLevels ||
      !this.appConfig.referralLevels.length
    ) {
      return 0;
    }

    const referralLevels = this.appConfig.referralLevels;
    for (let i = referralLevels.length - 1; i >= 0; i--) {
      const level = referralLevels[i];
      if (this.totalReferral >= level.minReferred) {
        return i;
      }
    }

    return 0;
  }

  public get remainingReferralsToNextLevel(): number {
    if (!this.hasRequested || undefined === this.statisticsData) {
      return 5;
    }

    const currentLevel = this.levelReferral;
    const level = this.appConfig.referralLevels[currentLevel];
    return level.minReferred - this.totalReferral;
  }

  public get nextLevelPercentage(): number {
    if (this.levelReferral === 0) {
      return 5;
    } else if (this.levelReferral === 1) {
      return 10;
    } else if (this.levelReferral === 2) {
      return 15;
    }

    return 5;
  }

  public get topActivities(): string[] {
    if (!this.hasRequested || undefined === this.statisticsData) {
      return ["Ride", "Swim"];
    }

    return this.statisticsData.topActivities ?? ["Ride", "Swim"];
  }

  /**
   * @todo missing method documentation
   */
  public get twoDigitsAmount() {
    const amount = this.balance;
    const withFourDecs = `${amount.toFixed(3).slice(0, -1)}`;
    const firstDigit = withFourDecs.slice(0, -2);
    const secondDigit = withFourDecs.slice(-2);

    return [firstDigit, secondDigit];
  }

  public get balance() {
    return this.web3 ? this.web3ActivBalance : this.nativeActivBalance / 100;
  }

  public get earlyAdoption() {
    return this.isEarlyAdopter;
  }

  /**
   * @todo missing method documentation
   */
  public async mounted() {
    if (!this.currentUserAddress) {
      await this.$store.dispatch("auth/fetchProfile");
    }
    const web3 = !Address.isValidRawAddress(this.currentUserAddress);
    this.$store.commit("auth/setWeb3", web3);
    await Promise.all([
      this.$store.dispatch(
        "statistics/fetchStatistics",
        this.currentUserAddress
      ),
      this.web3
        ? this.$store.dispatch(
            "web3/fetchActivBalance",
            this.currentUserAddress
          )
        : this.$store.dispatch(
            "statistics/fetchActivBalance",
            this.currentUserAddress
          ),
    ]);
    this.hasRequested = true;
  }
}
